import { Box, styled } from "@mui/material";
import {
  Alert,
  Checkbox,
  Col,
  DatePicker,
  Form,
  message,
  Modal,
  Radio,
  Row,
  Switch
} from "antd";
import locale from "antd/es/date-picker/locale/en_US";
import { FormInstance } from "antd/lib";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import { generateCode } from "../../utils/utils";
import { useMe } from "../../auth/MeContext";
import { PERM } from "../../auth/permissions";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import { FIELD_HEIGHT } from "../common/fields/fieldStyles";
import SelectField from "../common/fields/SelectField";
import "./addModal.scss";

interface SelectOption {
  value: number;
  label: string;
}

interface AddModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  reload?: () => void;
  isEdit?: boolean;
  couponData?: any;
}

const AddModal: React.FC<AddModalProps> = ({ open, setOpen, reload, isEdit = false, couponData }) => {
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };
  const formData = new FormData();
  const formRef = useRef<FormInstance>(null);
  const { can } = useMe();
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [salonOptions, setSalonOptions] = useState<SelectOption[]>([]);
  const [userList, setUserList] = useState([]);
  const [selectedDiscountType, setSelectedDiscountType] = useState("");
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const today = dayjs().startOf("day");

  const getCountries = async () => {
    await axiosInstance
      .get(pathAPI.couponLookupCountries)
      .then((response: any) => {
        setCountryOptions(
          (response.data?.data ?? []).map((item: any) => ({ value: item.id, label: item.name }))
        );
      })
      .catch(() => setCountryOptions([]));
  };

  // Salons of the chosen countries (dropdown country -> salon list)
  const loadSalons = async (countryIds: number[], keep: SelectOption[] = []) => {
    if (!countryIds || countryIds.length === 0) {
      setSalonOptions(keep);
      return;
    }
    await axiosInstance
      .get(pathAPI.couponLookupOutlets, { params: { countryIds: countryIds.join(",") } })
      .then((response: any) => {
        const loaded: SelectOption[] = (response.data?.data ?? []).map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        const extra = keep.filter((k) => !loaded.some((l) => l.value === k.value));
        setSalonOptions([...loaded, ...extra]);
      })
      .catch(() => setSalonOptions(keep));
  };

  const getUsers = async () => {
    await axiosInstance
      .get(pathAPI.appUsers)
      .then((res: any) => {
        const endData = res.data.data.map((item: any) => ({
          value: item.id,
          label: item.full_name || item.email
        }));
        setUserList(endData);
      })
      .catch((err: any) => {
        console.log("====== reload data table error: ", err);
        setUserList([]);
      });
  }

  const buildCouponPayload = () => {
    const form = formRef.current;
    if (!form) return {};
    
    return {
      active: form.getFieldValue("active") ?? false,
      coupon_name: form.getFieldValue("couponName"),
      coupon_code: form.getFieldValue("couponCode"),
      coupon_type: form.getFieldValue("couponType"),
      coupon_amount: Number(form.getFieldValue("discountAmount")),
      max_discount_per_customer: Number(form.getFieldValue("numberCouponPerUser")),
      number_of_coupon: Number(form.getFieldValue("totalCoupon")),
      start_time: new Date(form.getFieldValue("startTime")).toISOString(),
      end_time: new Date(form.getFieldValue("endTime")).toISOString(),
      description: form.getFieldValue("description"),
      apply_for_first_order_only: form.getFieldValue("applyToFirstOrderOnly"),
      app_user_ids: form.getFieldValue("appUserIds") || [],
      target_type: form.getFieldValue("targetType") || "COUNTRY",
      country_ids:
        form.getFieldValue("targetType") === "OUTLET" ? [] : form.getFieldValue("countryIds") || [],
      salon_ids:
        form.getFieldValue("targetType") === "OUTLET" ? form.getFieldValue("salonIds") || [] : [],
      min_spend: form.getFieldValue("miniumSpend") ? Number(form.getFieldValue("miniumSpend")) : undefined,
      max_spend: form.getFieldValue("maxiumSpend") ? Number(form.getFieldValue("maxiumSpend")) : undefined,
    };
  };

  const onFinish = async () => {
    try {
      await form.validateFields();
      setLoadingBtn(true);
      
      const payload = buildCouponPayload();
      console.log(isEdit ? 'Update coupon payload:' : 'Create coupon payload:', payload);
      
      if (isEdit) {
        updateCoupon(payload);
      } else {
        createCoupon(payload);
      }
    } catch (error) {
      console.log('Form validation failed:', error);
    }
  }

  const createCoupon = async (formData: any) => {
    try {
      const res = await axiosInstance.post(pathAPI.coupon, formData);
      message.success("Success");
      setOpen(false);
      formRef.current?.resetFields();
      setIsFormValid(false);
      if (reload) {
        reload();
      }
    } catch (err) {
      message.error("Failed");
    } finally {
      setLoadingBtn(false);
    }
  }

  const updateCoupon = async (formData: any) => {
    try {
      await axiosInstance.put(`${pathAPI.coupon}/${couponData?.id}`, formData);
      message.success("Coupon updated successfully");
      setOpen(false);
      formRef.current?.resetFields();
      setIsFormValid(false);
      if (reload) {
        reload();
      }
    } catch (err) {
      console.log('Update error:', err);
      message.error("Failed to update coupon");
    } finally {
      setLoadingBtn(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    formRef.current?.resetFields();
    setIsFormValid(false);
    if (isEdit && couponData) {
      setSelectedDiscountType("");
    }
  };

  const handleFormValuesChange = () => {
    setTimeout(() => {
      form.validateFields({ validateOnly: true })
        .then(() => {
          setIsFormValid(true);
        })
        .catch(() => {
          setIsFormValid(false);
        });
    }, 100);
  };

  const populateFormData = () => {
    if (couponData && formRef.current) {
      const startTime = dayjs(new Date(couponData.start_time).toISOString());
      const endTime = dayjs(new Date(couponData.end_time).toISOString());

      setTimeout(() => {
        form.setFieldsValue({
          couponName: couponData.coupon_name || couponData.name,
          couponCode: couponData.coupon_code || couponData.code,
          couponType: couponData.coupon_type || couponData.type,
          discountAmount: couponData.coupon_amount || couponData.amount,
          numberCouponPerUser: couponData.max_discount_per_customer || couponData.max_per_customer,
          totalCoupon: couponData.number_of_coupon || couponData.total_coupons,
          startTime: startTime,
          endTime: endTime,
          description: couponData.description,
          applyToFirstOrderOnly: !!couponData.apply_for_first_order_only,
          active: couponData.active === true || couponData.active === 1,
          miniumSpend: couponData.min_spend,
          maxiumSpend: couponData.max_spend,
          appUserIds: couponData.app_users ? couponData.app_users.map((item: any) => item.id) : [],
          salonIds: couponData.salons ? couponData.salons.map((item: any) => item.id) : [],
          targetType: couponData.target_type || "OUTLET",
          countryIds: couponData.countries ? couponData.countries.map((item: any) => item.id) : [],
        });
        if ((couponData.target_type || "OUTLET") === "OUTLET") {
          const current: SelectOption[] = (couponData.salons ?? []).map((item: any) => ({
            value: item.id,
            label: item.business_name || item.salon_name || item.name || String(item.id),
          }));
          loadSalons((couponData.countries ?? []).map((item: any) => item.id), current);
        }
        
        setSelectedDiscountType(couponData.coupon_type || couponData.type);
        setIsFormValid(true);
      }, 100);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && couponData) {
        populateFormData();
      } else {
        const randomCode = generateCode(12);
        setSalonOptions([]);
        setTimeout(() => {
          formRef.current?.setFieldValue("couponCode", randomCode);
          form.setFieldValue("couponCode", randomCode);
          form.setFieldValue("targetType", "COUNTRY");
          
          setTimeout(() => {
            form.validateFields({ validateOnly: true })
              .then(() => setIsFormValid(true))
              .catch(() => setIsFormValid(false));
          }, 200);
        }, 100);
      }
      setIsFormValid(false);
    } else {
      form.resetFields();
      setSelectedDiscountType("");
    }
  }, [open, form, isEdit, couponData]);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    if (open) {
      getCountries();
    }
  }, [open]);

  useEffect(() => {
    if (isEdit && couponData && userList.length > 0) {
      populateFormData();
    }
  }, [isEdit, couponData, userList]);

  const targetType = Form.useWatch("targetType", form);
  const needsApproval = !can(PERM.COUPON_APPROVE);

  return (
    <Modal
      title=""
      centered
      open={open}
      onOk={() => setOpen(false)}
      onCancel={handleCancel}
      width={1200}
      okText="Save"
      cancelText="Cancel"
      className="add_modal"
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={handleCancel}>{t("btn.cancel")}</CancelButton>
          <ActionButton
            onClick={onFinish}
            disabled={!isFormValid || loadingBtn}
            loading={loadingBtn}
          >
            {t("btn.ok")}
          </ActionButton>
        </Box>
      }
    >
      <div className="header_style">{isEdit ? 'Edit Coupon' : 'Add Coupon'}</div>
      {needsApproval && (
        <Alert
          type="info"
          showIcon
          style={{ margin: "8px 0" }}
          message={
            isEdit && couponData?.approval_status === "APPROVED"
              ? "Changing the discount, target, time or limits sends this coupon back to the Super Admin for approval."
              : "This coupon takes effect only after a Super Admin approves it."
          }
        />
      )}
      {isEdit && couponData?.approval_status === "REJECTED" && couponData?.reject_reason && (
        <Alert
          type="error"
          showIcon
          style={{ margin: "8px 0" }}
          message={`Rejected: ${couponData.reject_reason}`}
        />
      )}
      <StyledForm 
        {...layout} 
        ref={formRef} 
        onFinish={onFinish} 
        form={form}
        onValuesChange={handleFormValuesChange}
      >
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponName"}
                label="Coupon Name"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: 'Please enter coupon name' }]}
              >
                <InputField onChange={() => handleFormValuesChange()} />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponCode"}
                label="Coupon Code"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: 'Please enter coupon code' }]}
              >
                <InputField onChange={() => handleFormValuesChange()} />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"couponType"}
                label="Discount Type"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: 'Please select discount type' }]}
              >
                <SelectField
                  placeholder="Select Type"
                  searchable={false}
                  matchAnchorWidth
                  onChange={(value) => {
                    setSelectedDiscountType(String(value ?? ""));
                    handleFormValuesChange();
                  }}
                  options={[
                    {
                      value: "fixPrice",
                      label: "Fix Price",
                    },
                    {
                      value: "percent",
                      label: "Percent",
                    },
                  ]}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"discountAmount"}
                label={`Discounts Value ${selectedDiscountType === "fixPrice" ? "($)" : selectedDiscountType === "percent" ? "(%)" : ""}`}
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please enter discount amount' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue <= 0) {
                        return Promise.reject(new Error('Discount amount must be greater than 0'));
                      }
                      if (selectedDiscountType === "percent" && numValue > 100) {
                        return Promise.reject(new Error('Percentage cannot exceed 100%'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"miniumSpend"}
                label="Minimum Spend (Optional)"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 0) {
                        return Promise.reject(new Error('Minimum spend must be greater than or equal to 0'));
                      }
                      return Promise.resolve();
                    },
                  }
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"maxiumSpend"}
                label="Maximum Spend (Optional)"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 0) {
                        return Promise.reject(new Error('Maximum spend must be greater than or equal to 0'));
                      }
                      return Promise.resolve();
                    },
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const minSpend = getFieldValue('miniumSpend');
                      if (minSpend && Number(value) <= Number(minSpend)) {
                        return Promise.reject(new Error('Maximum spend must be greater than minimum spend'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"startTime"}
                label="Start Time"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: 'Please select start time' }]}
              >
                <DatePicker
                  format="YYYY/MM/DD HH:mm"
                  placeholder="Start Time"
                  style={{ width: "100%", height: FIELD_HEIGHT, borderRadius: 6 }}
                  locale={locale}
                  showTime={true}
                  disabledDate={(current: Dayjs) => {
                    return current && current < today;
                  }}
                  onChange={() => {
                    form.validateFields(['endTime']);
                    handleFormValuesChange();
                  }}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"endTime"}
                label="End Time"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please select end time' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const startTime = getFieldValue('startTime');
                      if (startTime && value && value.isBefore(startTime)) {
                        return Promise.reject(new Error('End time must be after start time'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  format="YYYY/MM/DD HH:mm"
                  placeholder="End Time"
                  style={{ width: "100%", height: FIELD_HEIGHT, borderRadius: 6 }}
                  locale={locale}
                  showTime={true}
                  // Disable all days before startTime
                  disabledDate={(current: Dayjs) => {
                    const startTime = form.getFieldValue('startTime');
                    if (!startTime) return false;
                    return current && current < dayjs(startTime).startOf('day');
                  }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"totalCoupon"}
                label="Number of Coupon"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please enter total number of coupons' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 1 || !Number.isInteger(numValue)) {
                        return Promise.reject(new Error('Must be a positive integer at least 1'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ min: "1" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
          <Col md={12} span={24}>
              <Form.Item
                name={"numberCouponPerUser"}
                label="Maximum number of coupons per customer"
                required
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  { required: true, message: 'Please enter maximum coupons per customer' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 1 || !Number.isInteger(numValue)) {
                        return Promise.reject(new Error('Must be a positive integer at least 1'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField
                  type="number"
                  inputProps={{ min: "1" }}
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={24} span={24}>
              <Form.Item
                name={"targetType"}
                label="Apply to"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                initialValue="COUNTRY"
              >
                <Radio.Group
                  onChange={(e) => {
                    form.setFieldsValue({ salonIds: [] });
                    setSalonOptions([]);
                    if (e.target.value === "OUTLET") {
                      loadSalons(form.getFieldValue("countryIds") || []);
                    }
                    handleFormValuesChange();
                  }}
                >
                  <Radio value="COUNTRY">All salons in countries</Radio>
                  <Radio value="OUTLET">Selected salons</Radio>
                </Radio.Group>
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"countryIds"}
                label={targetType === "OUTLET" ? "Country (to list salons)" : "Countries"}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "Please select at least one country",
                  },
                ]}
              >
                <SelectField
                  multiple
                  options={countryOptions}
                  placeholder="Select countries"
                  onChange={(values) => {
                    const ids = values as number[];
                    if (form.getFieldValue("targetType") === "OUTLET") {
                      const keep = salonOptions.filter((o) =>
                        (form.getFieldValue("salonIds") || []).includes(o.value)
                      );
                      loadSalons(ids, keep);
                    }
                    handleFormValuesChange();
                  }}
                />
              </Form.Item>
          </Col>
          {targetType === "OUTLET" && (
            <Col md={12} span={24}>
              <Form.Item
                name={"salonIds"}
                label="Salons"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    required: true,
                    type: "array",
                    min: 1,
                    message: "Please select at least one salon",
                  },
                ]}
              >
                <SelectField
                  multiple
                  options={salonOptions}
                  placeholder="Select salons"
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={12} span={24}>
              <Form.Item
                name={"appUserIds"}
                label="Target Customers (Optional)"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value.length > 0) {
                        if (!Array.isArray(value) || value.some(id => !id)) {
                          return Promise.reject(new Error('Please select valid customers'));
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <SelectField
                  multiple
                  options={userList}
                  placeholder="Select customers (optional)"
                  onChange={() => handleFormValuesChange()}
                />
              </Form.Item>
          </Col>
        </Row>
        <Row gutter={[6, 0]}>
          <Col md={24} span={24}>
              <Form.Item
                name={"description"}
                label="Description"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <InputField multiline minRows={5} />
              </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
              <Form.Item
                name="applyToFirstOrderOnly"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ marginBottom: 0, textAlign: 'left', display: 'flex', alignItems: 'center' }}
                valuePropName="checked"
              >
                <Checkbox id="checkBox" style={{ marginRight: 8 }}>
                  Applies to first order only
                </Checkbox>
              </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Active</span>
                <Form.Item
                  name={"active"}
                  style={{ margin: 0 }}
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
            </div>
          </Col>
        </Row>
      </StyledForm>
    </Modal>
  );
};

export default AddModal;
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 10px;
  }
`;
